"use client";

import { useState, useEffect } from "react";
import {
  Container,
  Card,
  CardBody,
  VStack,
  Text,
  Alert,
  AlertIcon,
  Button,
  Box,
} from "@chakra-ui/react";
import { supabase } from "../supabaseClient";
import OTPInput from "./OTPInput";
import { useNavigate, useLocation, Link } from "react-router-dom";

export default function OTPVerification({
  email,
  onVerificationComplete,
  onBackToSignIn,
}: {
  email: string;
  onVerificationComplete: () => void;
  onBackToSignIn: () => void;
}) {
  const [error, setError] = useState<string | null>(null);
  const [resendDisabled, setResendDisabled] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const navigate = useNavigate();
  const location = useLocation();
  const [success, setSuccess] = useState(false);

  const userEmail = email || location.state?.email || "";

  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    } else if (countdown === 0 && resendDisabled) {
      setResendDisabled(false);
    }
  }, [countdown, resendDisabled]);

  const handleVerify = async (otp: string) => {
    setError(null);

    try {
      const { error } = await supabase.auth.verifyOtp({
        email: userEmail,
        token: otp,
        type: "signup",
      });

      if (error) throw error;

      setSuccess(true);

      // Show success message for 2 seconds before redirecting
      setTimeout(() => {
        navigate("/login", {
          state: {
            message: "Email verified successfully! Please sign in to continue.",
          },
        });
      }, 2000);
    } catch (error: any) {
      setError(error.error_description || error.message);
    }
  };

  const handleResendOTP = async () => {
    setResendDisabled(true);
    setCountdown(60);
    setError(null);

    try {
      const { error } = await supabase.auth.resend({
        type: "signup",
        email: userEmail,
      });

      if (error) throw error;
    } catch (error: any) {
      setError(error.error_description || error.message);
      setResendDisabled(false);
      setCountdown(0);
    }
  };

  return (
    <Container maxW="md" py={12}>
      <Card>
        <CardBody p={8}>
          <VStack spacing={6}>
            <Box textAlign="center">
              <Box
                as="img"
                src="/main-logo.png"
                alt="CallSupplai Logo"
                h="60px"
                mx="auto"
                mb={2}
              />

              <Text color="secondary.600" fontSize="sm">
                We've sent a verification code to{" "}
                <Text as="span" color="blue.600">
                  {userEmail}
                </Text>
                . Please enter the code below.
              </Text>
            </Box>

            {error && (
              <Alert status="error" borderRadius="md" textColor={"red.600"}>
                <AlertIcon />
                {error}
              </Alert>
            )}

            {success && (
              <Alert status="success" borderRadius="md" textColor={"green.600"}>
                <AlertIcon />
                Email verified successfully! Redirecting to sign in...
              </Alert>
            )}

            <OTPInput onComplete={handleVerify} />

            <VStack spacing={3}>
              <Text fontSize="sm" color="gray.600">
                Didn't receive the code?{" "}
                <Button
                  variant="link"
                  colorScheme="primary"
                  onClick={handleResendOTP}
                  isDisabled={resendDisabled}
                  fontSize="sm"
                  p={0}
                  h="auto"
                  minW="auto"
                >
                  {resendDisabled ? `Resend in ${countdown}s` : "Resend OTP"}
                </Button>
              </Text>

              <Button
                as={Link}
                to="/login"
                variant="outline"
                w="100%"
                colorScheme="primary"
              >
                Back to Sign In
              </Button>
            </VStack>
          </VStack>
        </CardBody>
      </Card>
    </Container>
  );
}
